import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tooltip,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Logout as LogoutIcon,
  Save as SaveIcon,
  FilterAltOff as FilterAltOffIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CSV_COLUMNS, CSV_SECTIONS } from '../utils/csvColumns';

const DEFAULT_LEAD_STATUS = 'Active';
const DEFAULT_LEAD_STAGE = 'BD Pending';
const ALL_LEAD_STATUS = '';

const defaultFormState = CSV_COLUMNS.reduce((acc, col) => {
  acc[col.key] = col.type === 'boolean' ? false : '';
  return acc;
}, {});
defaultFormState.leadStatus = DEFAULT_LEAD_STATUS;
defaultFormState.leadStage = DEFAULT_LEAD_STAGE;
defaultFormState.assignedToUserId = '';

const OPERATION_STAGE_SECTIONS = [
  {
    title: 'Sales Operation',
    includeTotalLeads: true,
    stages: ['BD Pending', 'BOQ1 Pending', 'BOQ2 Pending', 'Booking Pending', 'Referral Check'],
  },
  {
    title: 'Design Operation',
    stages: ['Site Measurement Pending', 'Design & Quote Finalization Pending', 'Design Payment Pending', 'Push to Production Pending'],
  },
  {
    title: 'Site Operation',
    stages: ['Installation Payment Pending', 'Material Dispatch Pending', 'Installation ON-Going', 'Handover Done', 'Handover Payment Pending'],
  },
];

const normalizeStageName = (value) => String(value || '').trim().toLowerCase();

const DashboardPage = () => {
  const { user, api, logout } = useAuth();
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(DEFAULT_LEAD_STATUS);
  const [selectedLeadStageId, setSelectedLeadStageId] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [handoverTimes, setHandoverTimes] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [leadStages, setLeadStages] = useState([]);
  const [leadStageSummary, setLeadStageSummary] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [formState, setFormState] = useState(defaultFormState);

  const isAdminUser = String(user?.roleName || '').trim().toLowerCase() === 'admin';

  const byName = (items, key, value) => {
    const expected = String(value || '').trim().toLowerCase();
    if (!expected) {
      return undefined;
    }
    return items.find((item) => String(item?.[key] || '').trim().toLowerCase() === expected);
  };

  const getApiErrorMessage = (err, fallback) => {
    const details = err?.response?.data?.errors;
    if (details && typeof details === 'object') {
      const firstKey = Object.keys(details)[0];
      const firstValue = Array.isArray(details[firstKey]) ? details[firstKey][0] : details[firstKey];
      if (firstValue) {
        return String(firstValue);
      }
    }
    return err?.response?.data?.message || fallback;
  };

  const toOptionalId = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  const toApiDateTimeFromDateInput = (value) => {
    return value ? `${value}T00:00:00` : null;
  };

  const getDefaultAssignedToUserId = (users = assignableUsers) => {
    const currentUser = users.find((item) => String(item.userId) === String(user?.userId || ''));
    if (currentUser?.userId) {
      return String(currentUser.userId);
    }

    if (isAdminUser) {
      return '';
    }

    return users[0]?.userId ? String(users[0].userId) : '';
  };

  const getLeadStatusIdByStatus = (statusValue, statuses = leadStatuses) => {
    const expected = String(statusValue || '').trim().toLowerCase();
    if (!expected) {
      return null;
    }

    const status = statuses.find((item) => String(item?.status || '').trim().toLowerCase() === expected);
    return toOptionalId(status?.leadStatusId);
  };

  const getStatusChipSx = (statusValue) => {
    const status = String(statusValue || '').trim().toLowerCase();
    if (status === 'active') {
      return { backgroundColor: '#2e7d32', color: '#fff' };
    }
    if (status === 'inactive') {
      return { backgroundColor: '#d32f2f', color: '#fff' };
    }
    if (status === 'hold') {
      return { backgroundColor: '#edc948', color: '#1f1f1f' };
    }
    return undefined;
  };

  const normalizeClient = (client) => {
    return {
      clientId: client.clientId,
      sno: '',
      leadSource: client.sourceName || '',
      date: '',
      name: client.clientName || '',
      email: client.email || '',
      phoneNumber: client.phoneNumber || '',
      location: client.locationName || '',
      subLocation: client.subLocationName || '',
      scope: client.scopeName || '',
      subScope: client.subScopeName || '',
      handover: '',
      budget: client.budget || '',
      leadStatus: client.status || '',
      leadStage: client.stageName || '',
      briefingCall: false,
      briefingCallComments: '',
      bcDate: '',
      comments: client.notes || '',
      pitchPresentationM1SiteVisit: false,
      pitchPresentationM1Virtual: false,
      pitchPresentationM2SiteVisit: false,
      pitchPresentationM2Virtual: false,
      booking: false,
      assignedTo: client.assignedToName || '',
      lastActivityType: client.lastActivityType || '',
      lastComment: client.lastComment || '',
      createdAt: client.createdAt,
      updatedAt: client.updatedAt,
      raw: client,
    };
  };

  const fetchMasterData = async () => {
    try {
      const response = await api.get('/masterdata');
      const masterData = response.data?.data || {};

      setLeadStatuses(masterData.leadStatuses || []);
      setLocations(masterData.locations || []);
      setScopes(masterData.scopes || []);
      setLeadSources(masterData.leadSources || []);
      setLeadStages(masterData.leadStages || []);
      setHandoverTimes(masterData.handoverTimes || []);
      return masterData;
    } catch (err) {
      console.error('Failed to load master data', err);
      return {};
    }
  };

  const fetchAssignableUsers = async () => {
    try {
      const response = await api.get('/clients/assignable-users');
      const users = response.data?.data || [];
      setAssignableUsers(users);

      setFormState((prev) => {
        if (!prev) {
          return prev;
        }

        if (prev.assignedToUserId) {
          return prev;
        }

        return {
          ...prev,
          assignedToUserId: getDefaultAssignedToUserId(users),
        };
      });
    } catch (err) {
      console.error('Failed to load assignable users', err);
    }
  };

  const fetchClients = async (
    query = searchTerm,
    from = fromDate,
    to = toDate,
    leadStageId = selectedLeadStageId,
    statusValue = selectedStatus,
    statusOptions = leadStatuses
  ) => {
    setLoading(true);
    setError('');

    try {
      const leadStatusId = getLeadStatusIdByStatus(statusValue, statusOptions);
      const response = await api.get('/clients', {
        params: {
          searchTerm: query || undefined,
          leadStatusId: leadStatusId || undefined,
          leadStageId: leadStageId !== null && leadStageId !== undefined ? Number(leadStageId) : undefined,
          fromDate: from ? from.format('YYYY-MM-DD') : undefined,
          toDate: to ? to.format('YYYY-MM-DD') : undefined,
        },
      });

      const data = response.data?.data || [];
      setClients(data.map(normalizeClient));
      setInfo('');
    } catch (err) {
      console.error('Failed to load clients', err);
      setError('Failed to load clients from API. Please make sure backend is running.');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadStageSummary = async (
    query = searchTerm,
    from = fromDate,
    to = toDate,
    statusValue = selectedStatus,
    statusOptions = leadStatuses
  ) => {
    try {
      const leadStatusId = getLeadStatusIdByStatus(statusValue, statusOptions);
      const response = await api.get('/clients/lead-stage-summary', {
        params: {
          searchTerm: query || undefined,
          fromDate: from ? from.format('YYYY-MM-DD') : undefined,
          toDate: to ? to.format('YYYY-MM-DD') : undefined,
          leadStatusId: leadStatusId || undefined,
        },
      });

      setLeadStageSummary(response.data?.data || []);
    } catch (err) {
      console.error('Failed to load lead stage summary', err);
      setLeadStageSummary([]);
    }
  };

  useEffect(() => {
    const loadDashboard = async () => {
      const masterData = await fetchMasterData();
      const statusOptions = masterData?.leadStatuses || [];

      await Promise.all([
        fetchAssignableUsers(),
        fetchClients('', null, null, null, DEFAULT_LEAD_STATUS, statusOptions),
        fetchLeadStageSummary('', null, null, DEFAULT_LEAD_STATUS, statusOptions),
      ]);
    };

    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compute stats for new summary cards
  const stats = useMemo(() => {
    const summaryTotal = leadStageSummary.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
    return {
      totalLeads: leadStageSummary.length ? summaryTotal : clients.length,
    };
  }, [clients.length, leadStageSummary]);

  const leadStageCards = useMemo(() => {
    const countByLeadStageId = new Map(
      leadStageSummary.map((item) => [Number(item.leadStageId), Number(item.count) || 0])
    );

    return [...leadStages]
      .sort((a, b) => (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0))
      .map((stage) => ({
        leadStageId: stage.leadStageId,
        stageName: stage.stageName,
        count: countByLeadStageId.get(Number(stage.leadStageId)) ?? 0,
      }));
  }, [leadStages, leadStageSummary]);

  const leadStageSections = useMemo(() => {
    const cardsByName = new Map(
      leadStageCards.map((stage) => [normalizeStageName(stage.stageName), stage])
    );

    return OPERATION_STAGE_SECTIONS.map((section) => ({
      ...section,
      cards: section.stages
        .map((stageName) => cardsByName.get(normalizeStageName(stageName)))
        .filter(Boolean),
    }));
  }, [leadStageCards]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    fetchClients(value, fromDate, toDate, selectedLeadStageId, selectedStatus);
    fetchLeadStageSummary(value, fromDate, toDate, selectedStatus);
  };

  const handleStatusChange = (event) => {
    const value = event.target.value;
    setSelectedStatus(value);
    fetchClients(searchTerm, fromDate, toDate, selectedLeadStageId, value);
    fetchLeadStageSummary(searchTerm, fromDate, toDate, value);
  };

  const handleLeadStageFilterChange = (event) => {
    const value = event.target.value;
    const nextLeadStageId = value === '' ? null : Number(value);
    const nextStatus = nextLeadStageId && !selectedStatus ? DEFAULT_LEAD_STATUS : selectedStatus;
    setSelectedLeadStageId(nextLeadStageId);
    setSelectedStatus(nextStatus);
    fetchClients(searchTerm, fromDate, toDate, nextLeadStageId, nextStatus);
    fetchLeadStageSummary(searchTerm, fromDate, toDate, nextStatus);
  };

  const handleLeadStageCardClick = (leadStageId) => {
    const isTotalLeads = leadStageId === null || leadStageId === undefined;
    const nextLeadStageId = isTotalLeads ? null : selectedLeadStageId === leadStageId ? null : leadStageId;
    const nextStatus = isTotalLeads ? ALL_LEAD_STATUS : selectedStatus || DEFAULT_LEAD_STATUS;

    setSelectedLeadStageId(nextLeadStageId);
    setSelectedStatus(nextStatus);
    fetchClients(searchTerm, fromDate, toDate, nextLeadStageId, nextStatus);
    fetchLeadStageSummary(searchTerm, fromDate, toDate, nextStatus);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedStatus(DEFAULT_LEAD_STATUS);
    setSelectedLeadStageId(null);
    setFromDate(null);
    setToDate(null);
    fetchClients('', null, null, null, DEFAULT_LEAD_STATUS);
    fetchLeadStageSummary('', null, null, DEFAULT_LEAD_STATUS);
  };

  const openClient = (client) => {
    if (!client?.clientId) {
      return;
    }

    window.open(`/clients/${client.clientId}`, '_blank', 'noopener,noreferrer');
  };

  const openDeleteConfirm = (event, client) => {
    event.stopPropagation();
    setClientToDelete(client);
    setDeleteConfirmOpen(true);
    setError('');
    setInfo('');
  };

  const closeDeleteConfirm = () => {
    if (deleting) {
      return;
    }

    setDeleteConfirmOpen(false);
    setClientToDelete(null);
  };

  const closeToast = (_, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setToast((prev) => ({ ...prev, open: false }));
  };

  const handleDeleteClient = async () => {
    if (!clientToDelete?.clientId) {
      closeDeleteConfirm();
      return;
    }

    setDeleting(true);
    setError('');
    setInfo('');
    setToast((prev) => ({ ...prev, open: false }));

    try {
      await api.delete(`/clients/${clientToDelete.clientId}`);
      setDeleteConfirmOpen(false);
      setClientToDelete(null);
      setToast({ open: true, message: 'Client deleted successfully.', severity: 'success' });

      await Promise.all([
        fetchClients(searchTerm, fromDate, toDate, selectedLeadStageId, selectedStatus),
        fetchLeadStageSummary(searchTerm, fromDate, toDate, selectedStatus),
      ]);
    } catch (err) {
      console.error('Delete client failed', err);
      setError(getApiErrorMessage(err, 'Could not delete client. Please try again.'));
    } finally {
      setDeleting(false);
    }
  };

  const closeCreate = () => {
    setCreateOpen(false);
    setFormState({
      ...defaultFormState,
      assignedToUserId: getDefaultAssignedToUserId(),
    });
    setError('');
  };

  const openCreate = () => {
    setFormState((prev) => ({
      ...prev,
      assignedToUserId: prev.assignedToUserId || getDefaultAssignedToUserId(),
    }));
    setCreateOpen(true);
  };

  const handleCreateClient = async () => {
    if (!formState.name.trim()) {
      setError('Name is required.');
      return;
    }

    if (!isAdminUser && !toOptionalId(formState.assignedToUserId)) {
      setError('Assigned To is required.');
      return;
    }

    setSaving(true);
    setError('');
    setInfo('');

    const location = byName(locations, 'locationName', formState.location);
    const scope = byName(scopes, 'scopeName', formState.scope);
    const leadSource = byName(leadSources, 'sourceName', formState.leadSource);
    const leadStatus = byName(leadStatuses, 'status', formState.leadStatus) || byName(leadStatuses, 'status', 'Active');
    const leadStage = byName(leadStages, 'stageName', formState.leadStage);
    const handover = byName(handoverTimes, 'timeframe', formState.handover);

    const payload = {
      clientName: formState.name,
      email: formState.email || null,
      phoneNumber: formState.phoneNumber || null,
      alternatePhoneNumber: '',
      locationId: toOptionalId(location?.locationId),
      subLocationId: null,
      subLocationName: formState.subLocation || null,
      scopeId: toOptionalId(scope?.scopeId),
      subScopeId: null,
      budget: formState.budget || null,
      handoverTimeId: toOptionalId(handover?.handoverTimeId),
      leadSourceId: toOptionalId(leadSource?.leadSourceId),
      leadStatusId: toOptionalId(leadStatus?.leadStatusId),
      leadStageId: toOptionalId(leadStage?.leadStageId),
      assignedToUserId: toOptionalId(formState.assignedToUserId),
      notes: formState.comments || null,
      createdAt: toApiDateTimeFromDateInput(formState.date),
    };

    try {
      await api.post('/clients', payload);
      await Promise.all([
        fetchClients(searchTerm, fromDate, toDate, selectedLeadStageId, selectedStatus),
        fetchLeadStageSummary(searchTerm, fromDate, toDate, selectedStatus),
      ]);

      setInfo('Client saved successfully.');
      closeCreate();
    } catch (err) {
      console.error('Create client failed', err);
      setError(getApiErrorMessage(err, 'Could not create client. Please verify API connectivity and try again.'));
    } finally {
      setSaving(false);
    }
  };

  const renderCsvField = (column, state, stateSetter) => {
    const value = state[column.key];
    const onChangeValue = (nextValue) => {
      stateSetter((prev) => ({ ...prev, [column.key]: nextValue }));
    };

    if (column.type === 'boolean') {
      return (
        <Grid item xs={12} md={6} key={column.key}>
          <FormControlLabel
            control={<Switch checked={Boolean(value)} onChange={(e) => onChangeValue(e.target.checked)} />}
            label={column.label}
          />
        </Grid>
      );
    }

    const isSelectField =
      column.key === 'leadSource' ||
      column.key === 'leadStatus' ||
      column.key === 'leadStage' ||
      column.key === 'location' ||
      column.key === 'scope' ||
      column.key === 'handover';

    if (isSelectField) {
      let options = [];
      if (column.key === 'leadSource') options = leadSources.map((item) => item.sourceName);
      if (column.key === 'leadStatus') options = leadStatuses.map((item) => item.status);
      if (column.key === 'leadStage') options = leadStages.map((item) => item.stageName);
      if (column.key === 'location') options = locations.map((item) => item.locationName);
      if (column.key === 'scope') options = scopes.map((item) => item.scopeName);
      if (column.key === 'handover') options = handoverTimes.map((item) => item.timeframe);

      return (
        <Grid item xs={12} md={6} key={column.key}>
          <FormControl fullWidth>
            <InputLabel>{column.label}</InputLabel>
            <Select value={value} label={column.label} onChange={(e) => onChangeValue(e.target.value)}>
              {column.key !== 'leadStatus' && <MenuItem value="">Select</MenuItem>}
              {options.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      );
    }

    return (
      <Grid item xs={12} md={6} key={column.key}>
        <TextField
          fullWidth
          type={column.type === 'date' ? 'date' : column.type === 'email' ? 'email' : 'text'}
          label={column.label}
          required={column.required}
          value={value}
          onChange={(e) => onChangeValue(e.target.value)}
          InputLabelProps={column.type === 'date' ? { shrink: true } : undefined}
          multiline={column.key === 'comments' || column.key === 'briefingCallComments'}
          minRows={column.key === 'comments' || column.key === 'briefingCallComments' ? 2 : 1}
        />
      </Grid>
    );
  };

  const renderLastComment = (client) => {
    const comment = String(client?.lastComment || '').trim();
    if (!comment) {
      return '-';
    }

    const activity = String(client?.lastActivityType || '').trim() || 'Activity';
    return (
      <Tooltip
        arrow
        title={
          <Box>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
              {activity}
            </Typography>
            <Typography variant="caption">{comment}</Typography>
          </Box>
        }
      >
        <Typography
          variant="body2"
          sx={{
            display: 'block',
            maxWidth: 240,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {comment}
        </Typography>
      </Tooltip>
    );
  };

  const filteredClients = useMemo(() => {
    return clients;
  }, [clients]);

  const selectedStatusExists = leadStatuses.some((item) => item.status === selectedStatus);
  const statusFilterOptions =
    selectedStatus && !selectedStatusExists
      ? [{ leadStatusId: selectedStatus, status: selectedStatus }, ...leadStatuses]
      : leadStatuses;
  const isTotalLeadsSelected = selectedLeadStageId === null && selectedStatus === ALL_LEAD_STATUS;
  const hasActiveFilters = Boolean(
    searchTerm || selectedStatus !== DEFAULT_LEAD_STATUS || selectedLeadStageId || fromDate || toDate
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ minHeight: '100vh', pb: 4 }}>
      <Box
        sx={{
          px: 2,
          py: 4,
          mb: 3,
          color: '#f8f9ff',
          background: 'radial-gradient(circle at 10% 20%, #1f3a5f 0%, #0a101a 52%, #091423 100%)',
        }}
      >
        <Container maxWidth="xl">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                Sales Operations Board
              </Typography>
              <Typography sx={{ opacity: 0.84 }}>
                Welcome {user?.firstName} {user?.lastName} • CSV-aligned lead tracking workspace
              </Typography>
            </Box>
            <Button color="inherit" variant="outlined" startIcon={<LogoutIcon />} onClick={handleLogout}>
              Logout
            </Button>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl">
        <Stack spacing={3} sx={{ mb: 3 }}>
          {leadStageSections.map((section) => (
            <Box key={section.title}>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 800 }}>
                {section.title}
              </Typography>
              <Grid container spacing={2}>
                {section.includeTotalLeads && (
                  <Grid item xs={12} sm={6} md={2}>
                    <Card
                      onClick={() => handleLeadStageCardClick(null)}
                      sx={{
                        cursor: 'pointer',
                        border: isTotalLeadsSelected ? '2px solid #124e78' : '2px solid transparent',
                        boxShadow: isTotalLeadsSelected ? 6 : 1,
                        background: 'linear-gradient(135deg, #ffe8b0 0%, #ffc76d 100%)',
                        transition: 'all 140ms ease',
                      }}
                    >
                      <CardContent>
                        <Typography variant="overline">Total Leads</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800 }}>
                          {stats.totalLeads}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {section.cards.map((stage) => (
                  <Grid item xs={12} sm={6} md={2} key={stage.leadStageId}>
                    <Card
                      onClick={() => handleLeadStageCardClick(Number(stage.leadStageId))}
                      sx={{
                        cursor: 'pointer',
                        border: selectedLeadStageId === Number(stage.leadStageId) ? '2px solid #124e78' : '2px solid transparent',
                        boxShadow: selectedLeadStageId === Number(stage.leadStageId) ? 6 : 1,
                        background: 'linear-gradient(135deg, #d6fff5 0%, #77f7d1 100%)',
                        transition: 'all 140ms ease',
                      }}
                    >
                      <CardContent>
                        <Typography variant="overline">{stage.stageName}</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800 }}>
                          {stage.count}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Stack>

        <Paper sx={{ p: 2, borderRadius: 3 }}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, email, phone"
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Lead Status</InputLabel>
                <Select value={selectedStatus} label="Lead Status" onChange={handleStatusChange}>
                  <MenuItem value="">All</MenuItem>
                  {statusFilterOptions.map((item) => (
                    <MenuItem key={item.leadStatusId} value={item.status}>
                      {item.status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Lead Stage</InputLabel>
                <Select value={selectedLeadStageId ?? ''} label="Lead Stage" onChange={handleLeadStageFilterChange}>
                  <MenuItem value="">All</MenuItem>
                  {leadStages.map((stage) => (
                    <MenuItem key={stage.leadStageId} value={Number(stage.leadStageId)}>
                      {stage.stageName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <DateRangePicker
                value={[fromDate, toDate]}
                onChange={(newValue) => {
                  const [newFrom, newTo] = newValue;
                  setFromDate(newFrom);
                  setToDate(newTo);
                  fetchClients(searchTerm, newFrom, newTo, selectedLeadStageId, selectedStatus);
                  fetchLeadStageSummary(searchTerm, newFrom, newTo, selectedStatus);
                }}
                slots={{ field: SingleInputDateRangeField }}
                slotProps={{
                  textField: { size: 'small', fullWidth: true, label: 'Date Range' },
                }}
              />
            </Grid>
            <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
              <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', md: 'auto' }, justifyContent: 'flex-end' }}>
                <Tooltip title="Clear all filters">
                  <span>
                    <IconButton color="primary" onClick={handleClearFilters} disabled={!hasActiveFilters}>
                      <FilterAltOffIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={async () => {
                    await fetchAssignableUsers();
                    openCreate();
                  }}
                  sx={{ width: { xs: '100%', md: 'auto' }, fontWeight: 700 }}
                >
                  Add Client
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {info && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {info}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TableContainer>
            {loading ? (
              <Stack alignItems="center" sx={{ py: 5 }}>
                <CircularProgress />
              </Stack>
            ) : (
              <Table size="small" stickyHeader sx={{ minWidth: 1080 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Assigned To</TableCell>
                    <TableCell>Budget</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ width: 260 }}>Last Comment</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredClients.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography sx={{ py: 3, color: 'text.secondary' }}>No clients found.</Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {filteredClients.map((client) => (
                    <TableRow
                      key={client.clientId}
                      hover
                      onClick={() => openClient(client)}
                      sx={{ cursor: 'pointer', '& td': { whiteSpace: 'nowrap' } }}
                    >
                      <TableCell sx={{ fontWeight: 700 }}>{client.name || '-'}</TableCell>
                      <TableCell>{client.phoneNumber || '-'}</TableCell>
                      <TableCell>
                        {client.location || '-'}
                        {client.subLocation ? ` / ${client.subLocation}` : ''}
                      </TableCell>
                      <TableCell>{client.assignedTo || '-'}</TableCell>
                      <TableCell>{client.budget || '-'}</TableCell>
                      <TableCell>
                        <Chip label={client.leadStatus || 'Not Set'} size="small" sx={getStatusChipSx(client.leadStatus)} />
                      </TableCell>
                      <TableCell sx={{ width: 260, maxWidth: 260 }}>{renderLastComment(client)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={(event) => {
                              event.stopPropagation();
                              openClient(client);
                            }}
                          >
                            Open
                          </Button>
                          {isAdminUser && (
                            <Button
                              size="small"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={(event) => openDeleteConfirm(event, client)}
                              disabled={deleting}
                            >
                              Delete
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>

        </Paper>
      </Container>

      <Dialog open={createOpen} onClose={closeCreate} maxWidth="lg" fullWidth>
        <DialogTitle>Create Client (SalesSheet Columns)</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>
                Assignment
              </Typography>
              <Autocomplete
                options={assignableUsers}
                value={assignableUsers.find((item) => String(item.userId) === String(formState.assignedToUserId || '')) || null}
                onChange={(_, value) => {
                  setFormState((prev) => ({
                    ...prev,
                    assignedToUserId: value?.userId ? String(value.userId) : '',
                  }));
                }}
                getOptionLabel={(option) => `${option.firstName || ''} ${option.lastName || ''} (${option.roleName || '-'})`.trim()}
                isOptionEqualToValue={(option, value) => option.userId === value.userId}
                renderInput={(params) => <TextField {...params} label="Assigned To" placeholder="Search users" required={!isAdminUser} />}
                clearOnEscape
                disableClearable={!isAdminUser}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                {isAdminUser
                  ? 'Admin can assign to any active user or leave unassigned.'
                  : 'You can assign only to users allowed for your role.'}
              </Typography>
              <Divider sx={{ mt: 2 }} />
            </Grid>
            {CSV_SECTIONS.map((section) => (
              <Grid item xs={12} key={section}>
                <Typography variant="h6" sx={{ mb: 1.5 }}>
                  {section}
                </Typography>
                <Grid container spacing={2}>
                  {CSV_COLUMNS.filter((column) => column.section === section).map((column) => renderCsvField(column, formState, setFormState))}
                </Grid>
                <Divider sx={{ mt: 2 }} />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreate} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleCreateClient} variant="contained" startIcon={<SaveIcon />} disabled={saving}>
            {saving ? 'Saving...' : 'Create Client'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteConfirmOpen} onClose={closeDeleteConfirm} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete {clientToDelete?.name || 'this client'}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteClient} color="error" variant="contained" startIcon={<DeleteIcon />} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={closeToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={closeToast} severity={toast.severity} variant="filled" sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default DashboardPage;
